/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect } from "react";

export interface AnalyticsEvent {
  id: string;
  name: string;
  label: string;
  timestamp: string;
  view: string;
}

export interface AnalyticsSession {
  id: string;
  startedAt: string;
  lastActiveAt: string;
  device: "Desktop" | "Mobile" | "Tablet";
  browser: "Chrome" | "Safari" | "Firefox" | "Edge" | "Other";
  language: string;
  pageViewsCount: number;
  events: AnalyticsEvent[];
  views: string[];
}

export interface MetricSummary {
  totalPageViews: number;
  totalSessions: number;
  avgDurationSec: number;
  bounceRatePercent: number;
  conversionRatePercent: number;
}

// Global browser details
export function getDeviceType(): "Desktop" | "Mobile" | "Tablet" {
  if (typeof window === "undefined") return "Desktop";
  const width = window.innerWidth;
  if (width < 768) return "Mobile";
  if (width < 1024) return "Tablet";
  return "Desktop";
}

export function getBrowserName(): "Chrome" | "Safari" | "Firefox" | "Edge" | "Other" {
  if (typeof window === "undefined") return "Other";
  const ua = navigator.userAgent;
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("Chrome") && !ua.includes("Chromium")) return "Chrome";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Firefox")) return "Firefox";
  return "Other";
}

// Generate unique ID
function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
}

// Get or create Session ID for this active browser tab tab
export function getActiveSessionId(): string {
  if (typeof window === "undefined") return "";
  let id = sessionStorage.getItem("teralumax_active_session_id");
  if (!id) {
    id = generateId("sess");
    sessionStorage.setItem("teralumax_active_session_id", id);
  }
  return id;
}

// Save or merge session to local state
export function saveSessionState(session: AnalyticsSession) {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem("teralumax_analytics_sessions");
    let sessions: AnalyticsSession[] = [];
    if (raw) {
      try {
        sessions = JSON.parse(raw);
      } catch (e) {
        sessions = [];
      }
    }
    
    const idx = sessions.findIndex((s) => s.id === session.id);
    if (idx >= 0) {
      sessions[idx] = session;
    } else {
      sessions.unshift(session);
    }
    
    // Store back
    localStorage.setItem("teralumax_analytics_sessions", JSON.stringify(sessions.slice(0, 500))); // Max 500 historic sessions
    
    // Broadcast trigger for admin views
    window.dispatchEvent(new Event("storage"));
  } catch (err) {
    console.warn("Could not save session telemetry analytics", err);
  }
}

// Global tracking event function
export function trackEvent(name: string, label: string) {
  if (typeof window === "undefined") return;
  
  // Real Google Analytics event dispatch
  try {
    if ((window as any).gtag) {
      (window as any).gtag("event", name, {
        event_category: "Engagement",
        event_label: label,
      });
    }
  } catch (err) {
    console.debug("Failed forwarding event to Google Analytics SDK", err);
  }

  try {
    const sessId = getActiveSessionId();
    const raw = localStorage.getItem("teralumax_analytics_sessions");
    let sessions: AnalyticsSession[] = [];
    if (raw) {
      try {
        sessions = JSON.parse(raw);
      } catch (e) {
        sessions = [];
      }
    }

    let activeSess = sessions.find((s) => s.id === sessId);
    
    // Determine view
    let currentHash = "home";
    if (window.location.hash) {
      currentHash = window.location.hash.substring(1) || "home";
    }

    const newEvent: AnalyticsEvent = {
      id: generateId("evt"),
      name,
      label,
      timestamp: new Date().toISOString(),
      view: currentHash,
    };

    if (!activeSess) {
      activeSess = {
        id: sessId,
        startedAt: new Date().toISOString(),
        lastActiveAt: new Date().toISOString(),
        device: getDeviceType(),
        browser: getBrowserName(),
        language: navigator.language || "en",
        pageViewsCount: 1,
        events: [newEvent],
        views: [currentHash],
      };
    } else {
      activeSess.lastActiveAt = new Date().toISOString();
      activeSess.events.push(newEvent);
    }

    saveSessionState(activeSess);
  } catch (err) {
    console.warn("Failed tracking element interaction event", err);
  }
}

// Bootstrap dummy historic dashboard telemetry logs representing 14 production days
export function seedHistoricAnalytics() {
  if (typeof window === "undefined") return;
  const raw = localStorage.getItem("teralumax_analytics_sessions");
  if (raw && JSON.parse(raw).length > 20) return; // Already seeded

  const sessions: AnalyticsSession[] = [];
  const now = new Date();
  
  // Create mock sessions over the last 15 days
  const devices: Array<"Desktop" | "Mobile" | "Tablet"> = ["Desktop", "Mobile", "Tablet"];
  const deviceWeights = [0.65, 0.28, 0.07]; // 65% desktop, 28% mobile, 7% tablet
  
  const browsers: Array<"Chrome" | "Safari" | "Firefox" | "Edge" | "Other"> = ["Chrome", "Safari", "Firefox", "Edge", "Other"];
  const browserWeights = [0.60, 0.20, 0.10, 0.08, 0.02];
  
  const landingViews = ["home", "about-company", "leadership", "blog", "cases"];
  const viewWeights = [0.55, 0.15, 0.10, 0.12, 0.08];
  
  const languages = ["en", "es", "ja", "de", "hi", "fr"];
  const langWeights = [0.70, 0.10, 0.08, 0.05, 0.04, 0.03];

  function pickWeighted<T>(items: T[], weights: number[]): T {
    const r = Math.random();
    let sum = 0;
    for (let i = 0; i < items.length; i++) {
      sum += weights[i];
      if (r <= sum) return items[i];
    }
    return items[items.length - 1];
  }

  const sampleEvents = [
    { name: "scroll_deep", label: "Philosophy panel scrolled" },
    { name: "hover_product", label: "Inspected Roomy Product Showcase" },
    { name: "hover_product", label: "Inspected Siteforces Map Interactive" },
    { name: "expand_product", label: "ArrowEscape dynamic escape board run" },
    { name: "switch_lang", label: "Switched viewport language to ja" },
    { name: "form_success", label: "Secure contact lead submission dispatched" },
    { name: "read_article", label: "Expanded complete blog item view" },
    { name: "view_case", label: "Read architectural case study" },
  ];

  // Distribute ~150 simulated sessions
  for (let i = 14; i >= 0; i--) {
    const dayDate = new Date();
    dayDate.setDate(now.getDate() - i);
    
    // Higher traffic on weekdays
    const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
    const sessionCountForDay = isWeekend 
      ? Math.floor(6 + Math.random() * 6) // weekend
      : Math.floor(12 + Math.random() * 14); // weekday
      
    for (let s = 0; s < sessionCountForDay; s++) {
      // Set session time
      const sessionDate = new Date(dayDate);
      sessionDate.setHours(
        Math.floor(Math.random() * 24),
        Math.floor(Math.random() * 60),
        Math.floor(Math.random() * 60)
      );

      const durationSec = Math.floor(30 + Math.random() * 450); // 30s to 8min
      const lastActive = new Date(sessionDate.getTime() + durationSec * 1000);
      
      const sessionDev = pickWeighted(devices, deviceWeights);
      const sessionBrow = pickWeighted(browsers, browserWeights);
      const sessionLang = pickWeighted(languages, langWeights);
      
      // Page Views count
      const bounce = Math.random() < 0.28; // 28% bounce rate
      const viewsCount = bounce ? 1 : Math.floor(2 + Math.random() * 5);
      
      const sessionViews: string[] = [];
      const landing = pickWeighted(landingViews, viewWeights);
      sessionViews.push(landing);
      
      if (!bounce) {
        for (let v = 1; v < viewsCount; v++) {
          const nextV = landingViews[Math.floor(Math.random() * landingViews.length)];
          if (!sessionViews.includes(nextV)) {
            sessionViews.push(nextV);
          }
        }
      }

      // Action events in this session
      const events: AnalyticsEvent[] = [];
      const eventCount = bounce ? 0 : Math.floor(1 + Math.random() * 4);
      for (let e = 0; e < eventCount; e++) {
        const pickedEvt = sampleEvents[Math.floor(Math.random() * sampleEvents.length)];
        const evtTime = new Date(sessionDate.getTime() + Math.random() * durationSec * 1000);
        events.push({
          id: `evt-seeded-${i}-${s}-${e}`,
          name: pickedEvt.name,
          label: pickedEvt.label,
          timestamp: evtTime.toISOString(),
          view: sessionViews[Math.floor(Math.random() * sessionViews.length)],
        });
      }

      sessions.push({
        id: `sess-seeded-${i}-${s}`,
        startedAt: sessionDate.toISOString(),
        lastActiveAt: lastActive.toISOString(),
        device: sessionDev,
        browser: sessionBrow,
        language: sessionLang,
        pageViewsCount: sessionViews.length,
        events,
        views: sessionViews,
      });
    }
  }

  localStorage.setItem("teralumax_analytics_sessions", JSON.stringify(sessions));
}

// React Tracker Hook to automatically observe views and navigation
export function useAnalyticsTracker(currentView: string) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Dynamically inject Google Analytics script
    const googleAnalyticsId = (import.meta as any).env?.VITE_GA_MEASUREMENT_ID || "G-TERALUMAX2026";
    if (googleAnalyticsId && typeof document !== "undefined") {
      if (!document.getElementById("google-analytics-script")) {
        const script1 = document.createElement("script");
        script1.id = "google-analytics-script";
        script1.async = true;
        script1.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
        document.head.appendChild(script1);

        const script2 = document.createElement("script");
        script2.id = "google-analytics-init";
        script2.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${googleAnalyticsId}', { 'send_page_view': false });
          window.gtag = gtag;
        `;
        document.head.appendChild(script2);
      }
    }

    // Make sure historic logs exist
    seedHistoricAnalytics();

    const sessId = getActiveSessionId();
    const raw = localStorage.getItem("teralumax_analytics_sessions");
    let sessions: AnalyticsSession[] = [];
    if (raw) {
      try {
        sessions = JSON.parse(raw);
      } catch (e) {
        sessions = [];
      }
    }

    let activeSess = sessions.find((s) => s.id === sessId);
    const viewName = currentView || "home";

    if (!activeSess) {
      activeSess = {
        id: sessId,
        startedAt: new Date().toISOString(),
        lastActiveAt: new Date().toISOString(),
        device: getDeviceType(),
        browser: getBrowserName(),
        language: navigator.language || "en",
        pageViewsCount: 1,
        events: [],
        views: [viewName],
      };
    } else {
      activeSess.lastActiveAt = new Date().toISOString();
      if (!activeSess.views.includes(viewName)) {
        activeSess.views.push(viewName);
      }
      activeSess.pageViewsCount = (activeSess.pageViewsCount || 0) + 1;
    }

    // Capture style navigation in Google Analytics
    try {
      if ((window as any).gtag) {
        (window as any).gtag("config", googleAnalyticsId, {
          page_path: `#${viewName}`,
          page_title: `Teralumax - ${viewName}`,
        });
      }
    } catch (err) {
      console.debug("Failed sending pageview config to Google Analytics SDK", err);
    }

    // Capture standard automatic navigation event
    const navEvent: AnalyticsEvent = {
      id: generateId("evt-nav"),
      name: "page_view",
      label: `Navigated to dashboard view: ${viewName}`,
      timestamp: new Date().toISOString(),
      view: viewName,
    };
    activeSess.events.push(navEvent);

    saveSessionState(activeSess);

    // Keep active session alive by updating lastActiveAt periodically
    const heartbeat = setInterval(() => {
      const liveRaw = localStorage.getItem("teralumax_analytics_sessions");
      if (liveRaw) {
        try {
          const liveSessions: AnalyticsSession[] = JSON.parse(liveRaw);
          const s = liveSessions.find((x) => x.id === sessId);
          if (s) {
            s.lastActiveAt = new Date().toISOString();
            saveSessionState(s);
          }
        } catch (e) {}
      }
    }, 30000); // 30s heartbeat

    return () => clearInterval(heartbeat);
  }, [currentView]);
}

// Global hook error handler as an "essential industry production" element
export function useRuntimeErrorHandler() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const errorHandler = (event: ErrorEvent) => {
      const errorMsg = `JS Error: ${event.message || "Unknown error"} at ${event.filename || ""}:${event.lineno || 0}`;
      trackEvent("client_error", errorMsg);
      console.warn("Telemetry logger registered unhandled runtime error successfully:", errorMsg);
    };

    const rejectionHandler = (event: PromiseRejectionEvent) => {
      const reason = event.reason ? (event.reason.message || String(event.reason)) : "Unspecified rejection";
      const errorMsg = `Promise Rejection: ${reason}`;
      trackEvent("client_error", errorMsg);
      console.warn("Telemetry logger registered unhandled promise rejection successfully:", errorMsg);
    };

    window.addEventListener("error", errorHandler);
    window.addEventListener("unhandledrejection", rejectionHandler);

    return () => {
      window.removeEventListener("error", errorHandler);
      window.removeEventListener("unhandledrejection", rejectionHandler);
    };
  }, []);
}

// Combined silent component
export function AnalyticsTracker({ currentView }: { currentView: string }) {
  useAnalyticsTracker(currentView);
  useRuntimeErrorHandler();
  return null;
}
